import {
  BadgeConfirmationSubtle,
  BadgeNeutralSubtle,
  ButtonSmallError,
  ButtonSmallPrimary,
  ButtonSmallSubtle,
  Icon,
  IconSVG,
  SVGColorKey,
} from "@studio3t/generic-components";
import { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { SkeletonBar, SkeletonPill } from "@/components/Skeleton";
import {
  useAssignToken,
  useCreateToken,
  useRevokeToken,
  useTokens,
  useUnassignToken,
} from "@/hooks/useApi";
import type { TokenSummary } from "@/types/api";
import { Card, EmptyState, Section, SectionTitle } from "./styles";

interface TokensSectionProps {
  serviceName: string;
  routes: string[];
}

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback;
}

function useFlashError() {
  const [error, setError] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const flash = useCallback((msg: string) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setError(msg);
    timerRef.current = setTimeout(() => setError(null), 5000);
  }, []);

  return { error, flash };
}

function useConfirmOnce(timeoutMs = 3000) {
  const [pendingId, setPendingId] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const confirm = useCallback(
    (id: string): boolean => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (pendingId === id) {
        setPendingId(null);
        return true;
      }
      setPendingId(id);
      timerRef.current = setTimeout(() => setPendingId(null), timeoutMs);
      return false;
    },
    [pendingId, timeoutMs],
  );

  return { pendingId, confirm };
}

function useTokenCreate(serviceName: string, flash: (msg: string) => void) {
  const { mutateAsync: createToken, isPending: creating } = useCreateToken();
  const [label, setLabel] = useState("");
  const [revealedToken, setRevealedToken] = useState<string | null>(null);

  const handleCreate = useCallback(async () => {
    if (creating) return;
    const trimmed = label.trim();
    if (!trimmed) return;
    try {
      const created = await createToken({
        serviceName,
        req: { label: trimmed },
      });
      if (created?.token) setRevealedToken(created.token);
      setLabel("");
    } catch (err) {
      flash(errorMessage(err, "Failed to create token"));
    }
  }, [serviceName, label, creating, createToken, flash]);

  const dismissReveal = useCallback(() => setRevealedToken(null), []);

  return {
    creating,
    label,
    setLabel,
    revealedToken,
    handleCreate,
    dismissReveal,
  };
}

function useTokenOps(
  serviceName: string,
  routes: string[],
  flash: (msg: string) => void,
) {
  const { mutateAsync: revokeToken, isPending: revoking } = useRevokeToken();
  const { mutateAsync: assignToken, isPending: assigning } = useAssignToken();
  const { mutateAsync: unassignToken, isPending: unassigning } =
    useUnassignToken();
  const [assigningId, setAssigningId] = useState<string | null>(null);
  const [assignRoute, setAssignRoute] = useState("");
  const { pendingId: confirmRevokeId, confirm: confirmRevoke } =
    useConfirmOnce();

  const handleRevoke = useCallback(
    async (tokenId: string) => {
      if (revoking) return;
      if (!confirmRevoke(tokenId)) return;
      try {
        await revokeToken({ serviceName, tokenId });
      } catch (err) {
        flash(errorMessage(err, "Failed to revoke token"));
      }
    },
    [serviceName, revoking, confirmRevoke, revokeToken, flash],
  );

  const handleAssign = useCallback(
    async (tokenId: string) => {
      if (assigning || !assignRoute) return;
      try {
        await assignToken({
          serviceName,
          tokenId,
          req: { resource: assignRoute },
        });
        setAssigningId(null);
        setAssignRoute("");
      } catch (err) {
        flash(errorMessage(err, "Failed to assign token"));
      }
    },
    [serviceName, assigning, assignRoute, assignToken, flash],
  );

  const handleUnassign = useCallback(
    async (tokenId: string) => {
      if (unassigning) return;
      try {
        await unassignToken({ serviceName, tokenId });
      } catch (err) {
        flash(errorMessage(err, "Failed to unassign token"));
      }
    },
    [serviceName, unassigning, unassignToken, flash],
  );

  const startAssign = useCallback(
    (tokenId: string) => {
      setAssigningId(tokenId);
      setAssignRoute(routes[0] ?? "");
    },
    [routes],
  );

  const cancelAssign = useCallback(() => setAssigningId(null), []);

  return {
    assigningId,
    assignRoute,
    setAssignRoute,
    confirmRevokeId,
    handleRevoke,
    handleAssign,
    handleUnassign,
    startAssign,
    cancelAssign,
  };
}

function useTokenActions(serviceName: string, routes: string[]) {
  // Token mutations invalidate queryKeys.tokens(serviceName), which refetches
  // every observer of this query (here and the parent), so no manual refetch is
  // needed.
  const { data: tokens, isLoading: tokensLoading } = useTokens(serviceName);
  const { error, flash } = useFlashError();

  const create = useTokenCreate(serviceName, flash);
  const ops = useTokenOps(serviceName, routes, flash);

  return { tokens, tokensLoading, error, ...create, ...ops };
}

function CreateTokenInput({
  label,
  setLabel,
  onCreate,
  disabled,
}: {
  label: string;
  setLabel: (v: string) => void;
  onCreate: () => void;
  disabled: boolean;
}) {
  return (
    <CreateRow>
      <LabelInput
        type="text"
        placeholder="Token label"
        aria-label="Token label"
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && onCreate()}
      />
      <ButtonSmallPrimary
        text="Create"
        onClick={onCreate}
        disabled={disabled}
      />
    </CreateRow>
  );
}

function TokenRevealBanner({
  token,
  onDismiss,
}: {
  token: string;
  onDismiss: () => void;
}) {
  return (
    <RevealBanner>
      <RevealLabel>
        <IconSVG
          icon={Icon.Warning}
          svgColorKey={SVGColorKey.semanticTextBody}
          size={14}
        />
        Copy this token now — it won't be shown again
      </RevealLabel>
      <TokenValue>{token}</TokenValue>
      <RevealActions>
        <ButtonSmallSubtle
          text="Copy"
          onClick={() => {
            if (navigator.clipboard?.writeText) {
              navigator.clipboard.writeText(token).catch(() => {
                window.prompt("Copy this token:", token);
              });
            } else {
              window.prompt("Copy this token:", token);
            }
          }}
        />
        <ButtonSmallSubtle text="Dismiss" onClick={onDismiss} />
      </RevealActions>
    </RevealBanner>
  );
}

function TokenRowItem({
  token,
  routes,
  confirmRevokeId,
  onRevoke,
  onAssign,
  onUnassign,
  onStartAssign,
  assigningId,
  assignRoute,
  setAssignRoute,
  onCancelAssign,
}: {
  token: TokenSummary;
  routes: string[];
  confirmRevokeId: string | null;
  onRevoke: (id: string) => void;
  onAssign: (id: string) => void;
  onUnassign: (id: string) => void;
  onStartAssign: (id: string) => void;
  assigningId: string | null;
  assignRoute: string;
  setAssignRoute: (v: string) => void;
  onCancelAssign: () => void;
}) {
  return (
    <TokenRow>
      <TokenInfo>
        <TokenLabel>{token.label}</TokenLabel>
        <TokenPrefix>{token.prefix}…</TokenPrefix>
        <ResourceBadge resource={token.resource} />
      </TokenInfo>
      <TokenActions>
        {assigningId === token.id ? (
          <AssignRow>
            <RouteSelect
              aria-label="Route"
              value={assignRoute}
              onChange={(e) => setAssignRoute(e.target.value)}
            >
              {routes.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </RouteSelect>
            <ButtonSmallPrimary
              text="Assign"
              onClick={() => onAssign(token.id)}
              disabled={!assignRoute}
            />
            <ButtonSmallSubtle text="Cancel" onClick={onCancelAssign} />
          </AssignRow>
        ) : (
          <TokenActionButtons
            token={token}
            routes={routes}
            confirmRevokeId={confirmRevokeId}
            onRevoke={onRevoke}
            onUnassign={onUnassign}
            onStartAssign={onStartAssign}
          />
        )}
      </TokenActions>
    </TokenRow>
  );
}

function ResourceBadge({ resource }: { resource?: string | null }) {
  if (!resource) return <BadgeNeutralSubtle>detached</BadgeNeutralSubtle>;
  return (
    <BadgeConfirmationSubtle>
      <IconSVG
        icon={Icon.Link}
        svgColorKey={SVGColorKey.semanticTextBody}
        size={10}
      />{" "}
      {resource}
    </BadgeConfirmationSubtle>
  );
}

function TokenActionButtons({
  token,
  routes,
  confirmRevokeId,
  onRevoke,
  onUnassign,
  onStartAssign,
}: {
  token: TokenSummary;
  routes: string[];
  confirmRevokeId: string | null;
  onRevoke: (id: string) => void;
  onUnassign: (id: string) => void;
  onStartAssign: (id: string) => void;
}) {
  return (
    <>
      {token.resource ? (
        <ButtonSmallSubtle
          text="Unassign"
          onClick={() => onUnassign(token.id)}
        />
      ) : (
        <ButtonSmallSubtle
          text="Assign"
          onClick={() => onStartAssign(token.id)}
          disabled={routes.length === 0}
        />
      )}
      <ButtonSmallError
        text={confirmRevokeId === token.id ? "Confirm Revoke" : "Revoke"}
        onClick={() => onRevoke(token.id)}
      />
    </>
  );
}

// Shape of a token row while loading: label bar + status-badge pill.
const TOKEN_SKELETON_ROWS = Array.from(
  { length: 2 },
  (_, i) => `skeleton-${i}`,
);

export function TokensSection({ serviceName, routes }: TokensSectionProps) {
  const mgmt = useTokenActions(serviceName, routes);

  return (
    <Section>
      <SectionTitle>
        <IconSVG
          icon={Icon.Lock}
          svgColorKey={SVGColorKey.semanticTextBody}
          size={14}
        />{" "}
        API Tokens
      </SectionTitle>
      <Card>
        <CreateTokenInput
          label={mgmt.label}
          setLabel={mgmt.setLabel}
          onCreate={mgmt.handleCreate}
          disabled={mgmt.creating || !mgmt.label.trim()}
        />

        {mgmt.revealedToken && (
          <TokenRevealBanner
            token={mgmt.revealedToken}
            onDismiss={mgmt.dismissReveal}
          />
        )}

        {/* A fetch in flight is not "no tokens" — skeleton until it resolves. */}
        {mgmt.tokensLoading ? (
          <TokenList aria-hidden="true">
            {TOKEN_SKELETON_ROWS.map((key) => (
              <TokenSkeletonRow key={key}>
                <SkeletonBar $width="30%" />
                <SkeletonPill $width="72px" />
              </TokenSkeletonRow>
            ))}
          </TokenList>
        ) : mgmt.tokens && mgmt.tokens.length > 0 ? (
          <TokenList>
            {mgmt.tokens.map((t) => (
              <TokenRowItem
                key={t.id}
                token={t}
                routes={routes}
                confirmRevokeId={mgmt.confirmRevokeId}
                onRevoke={mgmt.handleRevoke}
                onAssign={mgmt.handleAssign}
                onUnassign={mgmt.handleUnassign}
                onStartAssign={mgmt.startAssign}
                assigningId={mgmt.assigningId}
                assignRoute={mgmt.assignRoute}
                setAssignRoute={mgmt.setAssignRoute}
                onCancelAssign={mgmt.cancelAssign}
              />
            ))}
          </TokenList>
        ) : (
          <EmptyState style={{ padding: 24 }}>
            No tokens created. Create a token and assign it to a route to
            require API key authentication on ingest.
          </EmptyState>
        )}

        {mgmt.error && <ErrorBanner>{mgmt.error}</ErrorBanner>}
      </Card>
    </Section>
  );
}

const CreateRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
`;

const LabelInput = styled.input`
  flex: 1;
  padding: 6px 10px;
  font-size: 12px;
  border: 1px solid ${(p) => p.theme.cards1Border};
  border-radius: 4px;
  background: ${(p) => p.theme.cards1Background};
  color: ${(p) => p.theme.semanticTextBody};
  outline: none;

  &:focus {
    border-color: ${(p) => p.theme.linkColor};
  }

  &::placeholder {
    color: ${(p) => p.theme.semanticTextDisabled};
  }
`;

const RevealBanner = styled.div`
  margin-bottom: 16px;
  padding: 12px;
  border: 1px solid #fcd34d;
  border-radius: 4px;
  background: #fefce8;
`;

const RevealLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #92400e;
  margin-bottom: 8px;
`;

const TokenValue = styled.code`
  display: block;
  padding: 8px;
  font-size: 12px;
  font-family: "JetBrains Mono", "Fira Code", monospace;
  background: #fff;
  border: 1px solid #fde68a;
  border-radius: 4px;
  word-break: break-all;
  color: #78350f;
  margin-bottom: 8px;
`;

const RevealActions = styled.div`
  display: flex;
  gap: 8px;
`;

const TokenList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

// Same row footprint as TokenRow, holding a label bar and a badge pill apart.
const TokenSkeletonRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
`;

const TokenRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  border: 1px solid ${(p) => p.theme.cards1Border};
  border-radius: 4px;
  gap: 12px;
`;

const TokenInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const TokenLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${(p) => p.theme.semanticTextHeading};
`;

const TokenPrefix = styled.code`
  font-size: 11px;
  font-family: "JetBrains Mono", "Fira Code", monospace;
  color: ${(p) => p.theme.semanticTextLight};
`;

const TokenActions = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
`;

const AssignRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const RouteSelect = styled.select`
  padding: 4px 8px;
  font-size: 12px;
  border: 1px solid ${(p) => p.theme.cards1Border};
  border-radius: 4px;
  background: ${(p) => p.theme.cards1Background};
  color: ${(p) => p.theme.semanticTextBody};
  outline: none;

  &:focus {
    border-color: ${(p) => p.theme.linkColor};
  }
`;

const ErrorBanner = styled.div`
  margin-top: 12px;
  padding: 8px 12px;
  font-size: 12px;
  color: #dc2626;
  background: #fee2e2;
  border: 1px solid #fecaca;
  border-radius: 4px;
`;
